import {
  BOARD_SIZE,
  UNIT_SIZE,
  boardFromValues,
  candidateMask,
  conflictingCells,
  isComplete,
  maskValues,
  peerIndexes,
  type Board,
  type CellValue,
} from './board';
import type { Difficulty } from './generator';

export type HumanTechnique =
  | 'nakedSingle'
  | 'hiddenSingle'
  | 'lockedCandidate'
  | 'nakedPair'
  | 'hiddenPair'
  | 'nakedTriple'
  | 'hiddenTriple'
  | 'xWing';

export type HumanStep = {
  technique: HumanTechnique;
  affectedCells: readonly number[];
};

export type HumanSolveResult = {
  board: Board;
  steps: readonly HumanStep[];
  hardestTechnique: HumanTechnique;
};

export type HumanAnalysis = {
  status: 'solved' | 'stalled' | 'invalid';
  board: Board;
  steps: readonly HumanStep[];
  hardestTechnique: HumanTechnique;
};

export type DifficultyRating = {
  difficulty: Difficulty;
  hardestTechnique: HumanTechnique;
  stepCount: number;
};

type LogicalState = {
  values: CellValue[];
  masks: number[];
};

const techniqueOrder: readonly HumanTechnique[] = [
  'nakedSingle',
  'hiddenSingle',
  'lockedCandidate',
  'nakedPair',
  'hiddenPair',
  'nakedTriple',
  'hiddenTriple',
  'xWing',
];
const MEDIUM_MAX_TECHNIQUE_INDEX = 4;

const rows = Array.from({ length: UNIT_SIZE }, (_, row) =>
  Array.from({ length: UNIT_SIZE }, (_, column) => row * UNIT_SIZE + column),
);
const columns = Array.from({ length: UNIT_SIZE }, (_, column) =>
  Array.from({ length: UNIT_SIZE }, (_, row) => row * UNIT_SIZE + column),
);
const boxes = Array.from({ length: UNIT_SIZE }, (_, box) =>
  Array.from(
    { length: UNIT_SIZE },
    (_, offset) =>
      (Math.floor(box / 3) * 3 + Math.floor(offset / 3)) * UNIT_SIZE + (box % 3) * 3 + (offset % 3),
  ),
);
const units: readonly (readonly number[])[] = [...rows, ...columns, ...boxes];

function countBits(mask: number): number {
  let remaining = mask;
  let count = 0;
  while (remaining !== 0) {
    remaining &= remaining - 1;
    count += 1;
  }
  return count;
}

function createState(board: Board): LogicalState {
  return {
    values: [...board],
    masks: board.map((value, index) => (value === 0 ? candidateMask(board, index) : 0)),
  };
}

function assign(state: LogicalState, index: number, value: CellValue): HumanStep {
  state.values[index] = value;
  state.masks[index] = 0;
  const bit = 1 << value;
  for (const peer of peerIndexes(index)) {
    state.masks[peer] = (state.masks[peer] ?? 0) & ~bit;
  }
  return { technique: 'nakedSingle', affectedCells: [index] };
}

function applyNakedSingle(state: LogicalState): HumanStep | null {
  for (let index = 0; index < BOARD_SIZE; index += 1) {
    const mask = state.masks[index] ?? 0;
    if (state.values[index] === 0 && countBits(mask) === 1) {
      return assign(state, index, maskValues(mask)[0] ?? 0);
    }
  }
  return null;
}

function applyHiddenSingle(state: LogicalState): HumanStep | null {
  for (const unit of units) {
    for (let value = 1; value <= UNIT_SIZE; value += 1) {
      const bit = 1 << value;
      const possible = unit.filter(
        (index) => state.values[index] === 0 && ((state.masks[index] ?? 0) & bit) !== 0,
      );
      if (possible.length === 1) {
        const index = possible[0] ?? 0;
        assign(state, index, value as CellValue);
        return { technique: 'hiddenSingle', affectedCells: [index] };
      }
    }
  }
  return null;
}

function eliminate(
  state: LogicalState,
  indexes: readonly number[],
  mask: number,
): readonly number[] {
  const affected: number[] = [];
  for (const index of indexes) {
    const previous = state.masks[index] ?? 0;
    const next = previous & ~mask;
    if (next !== previous) {
      state.masks[index] = next;
      affected.push(index);
    }
  }
  return affected;
}

function applyLockedCandidate(state: LogicalState): HumanStep | null {
  for (let box = 0; box < UNIT_SIZE; box += 1) {
    const boxIndexes = boxes[box] ?? [];
    for (let value = 1; value <= UNIT_SIZE; value += 1) {
      const bit = 1 << value;
      const possible = boxIndexes.filter((index) => ((state.masks[index] ?? 0) & bit) !== 0);
      if (possible.length < 2) {
        continue;
      }
      const first = possible[0] ?? 0;
      const row = Math.floor(first / UNIT_SIZE);
      if (possible.every((index) => Math.floor(index / UNIT_SIZE) === row)) {
        const affected = eliminate(
          state,
          (rows[row] ?? []).filter((index) => !boxIndexes.includes(index)),
          bit,
        );
        if (affected.length > 0) {
          return { technique: 'lockedCandidate', affectedCells: affected };
        }
      }
      const column = first % UNIT_SIZE;
      if (possible.every((index) => index % UNIT_SIZE === column)) {
        const affected = eliminate(
          state,
          (columns[column] ?? []).filter((index) => !boxIndexes.includes(index)),
          bit,
        );
        if (affected.length > 0) {
          return { technique: 'lockedCandidate', affectedCells: affected };
        }
      }
    }
  }

  for (const line of [...rows, ...columns]) {
    for (let value = 1; value <= UNIT_SIZE; value += 1) {
      const bit = 1 << value;
      const possible = line.filter((index) => ((state.masks[index] ?? 0) & bit) !== 0);
      if (possible.length < 2) {
        continue;
      }
      const firstBox = boxes.findIndex((box) => box.includes(possible[0] ?? 0));
      if (firstBox >= 0 && possible.every((index) => (boxes[firstBox] ?? []).includes(index))) {
        const affected = eliminate(
          state,
          (boxes[firstBox] ?? []).filter((index) => !line.includes(index)),
          bit,
        );
        if (affected.length > 0) {
          return { technique: 'lockedCandidate', affectedCells: affected };
        }
      }
    }
  }
  return null;
}

function combinations<T>(values: readonly T[], size: number): readonly (readonly T[])[] {
  const result: T[][] = [];
  const visit = (start: number, chosen: T[]): void => {
    if (chosen.length === size) {
      result.push(chosen);
      return;
    }
    for (let index = start; index <= values.length - (size - chosen.length); index += 1) {
      const value = values[index];
      if (value !== undefined) {
        visit(index + 1, [...chosen, value]);
      }
    }
  };
  visit(0, []);
  return result;
}

function applyNakedSubset(state: LogicalState, size: 2 | 3): HumanStep | null {
  for (const unit of units) {
    const candidates = unit.filter((index) => {
      const count = countBits(state.masks[index] ?? 0);
      return count >= 2 && count <= size;
    });
    for (const subset of combinations(candidates, size)) {
      const union = subset.reduce((mask, index) => mask | (state.masks[index] ?? 0), 0);
      if (countBits(union) !== size) {
        continue;
      }
      const confinedCells = unit.filter((index) => {
        const mask = state.masks[index] ?? 0;
        return mask !== 0 && (mask & ~union) === 0;
      });
      if (confinedCells.length !== size) {
        continue;
      }
      const affected = eliminate(
        state,
        unit.filter((index) => !subset.includes(index)),
        union,
      );
      if (affected.length > 0) {
        return {
          technique: size === 2 ? 'nakedPair' : 'nakedTriple',
          affectedCells: affected,
        };
      }
    }
  }
  return null;
}

function applyHiddenSubset(state: LogicalState, size: 2 | 3): HumanStep | null {
  const digits = Array.from({ length: UNIT_SIZE }, (_, index) => index + 1);
  for (const unit of units) {
    for (const digitSubset of combinations(digits, size)) {
      const digitMask = digitSubset.reduce((mask, value) => mask | (1 << value), 0);
      const cells = unit.filter((index) => ((state.masks[index] ?? 0) & digitMask) !== 0);
      if (
        cells.length !== size ||
        !digitSubset.every((value) =>
          cells.some((index) => ((state.masks[index] ?? 0) & (1 << value)) !== 0),
        )
      ) {
        continue;
      }
      const affected: number[] = [];
      for (const index of cells) {
        const previous = state.masks[index] ?? 0;
        const next = previous & digitMask;
        if (next !== previous) {
          state.masks[index] = next;
          affected.push(index);
        }
      }
      if (affected.length > 0) {
        return {
          technique: size === 2 ? 'hiddenPair' : 'hiddenTriple',
          affectedCells: affected,
        };
      }
    }
  }
  return null;
}

function applyXWingOrientation(
  state: LogicalState,
  primaryUnits: readonly (readonly number[])[],
  secondaryPosition: (index: number) => number,
): HumanStep | null {
  for (let value = 1; value <= UNIT_SIZE; value += 1) {
    const bit = 1 << value;
    const lines = primaryUnits
      .map((unit, line) => ({
        line,
        positions: unit
          .filter((index) => ((state.masks[index] ?? 0) & bit) !== 0)
          .map(secondaryPosition),
      }))
      .filter(({ positions }) => positions.length === 2);
    for (const pair of combinations(lines, 2)) {
      const first = pair[0];
      const second = pair[1];
      if (
        first === undefined ||
        second === undefined ||
        first.positions[0] !== second.positions[0] ||
        first.positions[1] !== second.positions[1]
      ) {
        continue;
      }
      const corners = new Set([
        ...(primaryUnits[first.line] ?? []).filter((index) =>
          first.positions.includes(secondaryPosition(index)),
        ),
        ...(primaryUnits[second.line] ?? []).filter((index) =>
          second.positions.includes(secondaryPosition(index)),
        ),
      ]);
      const targets = Array.from({ length: BOARD_SIZE }, (_, index) => index).filter(
        (index) =>
          first.positions.includes(secondaryPosition(index)) &&
          !corners.has(index) &&
          ((state.masks[index] ?? 0) & bit) !== 0,
      );
      const affected = eliminate(state, targets, bit);
      if (affected.length > 0) {
        return { technique: 'xWing', affectedCells: affected };
      }
    }
  }
  return null;
}

function applyXWing(state: LogicalState): HumanStep | null {
  return (
    applyXWingOrientation(state, rows, (index) => index % UNIT_SIZE) ??
    applyXWingOrientation(state, columns, (index) => Math.floor(index / UNIT_SIZE))
  );
}

function hasImpossibleCell(state: LogicalState): boolean {
  return state.values.some((value, index) => value === 0 && state.masks[index] === 0);
}

function hardestTechnique(steps: readonly HumanStep[]): HumanTechnique {
  let hardest = 'nakedSingle' as HumanTechnique;
  for (const step of steps) {
    if (techniqueOrder.indexOf(step.technique) > techniqueOrder.indexOf(hardest)) {
      hardest = step.technique;
    }
  }
  return hardest;
}

export function analyzeWithHumanTechniques(board: Board): HumanAnalysis {
  if (conflictingCells(board).size > 0) {
    return {
      status: 'invalid',
      board,
      steps: [],
      hardestTechnique: 'nakedSingle',
    };
  }
  const state = createState(board);
  const steps: HumanStep[] = [];

  while (!state.values.every((value) => value !== 0)) {
    if (hasImpossibleCell(state)) {
      return {
        status: 'invalid',
        board: boardFromValues(state.values),
        steps,
        hardestTechnique: hardestTechnique(steps),
      };
    }
    const step =
      applyNakedSingle(state) ??
      applyHiddenSingle(state) ??
      applyLockedCandidate(state) ??
      applyNakedSubset(state, 2) ??
      applyHiddenSubset(state, 2) ??
      applyNakedSubset(state, 3) ??
      applyHiddenSubset(state, 3) ??
      applyXWing(state);
    if (step === null) {
      return {
        status: 'stalled',
        board: boardFromValues(state.values),
        steps,
        hardestTechnique: hardestTechnique(steps),
      };
    }
    steps.push(step);
  }

  const solvedBoard = boardFromValues(state.values);
  if (!isComplete(solvedBoard)) {
    return {
      status: 'invalid',
      board: solvedBoard,
      steps,
      hardestTechnique: hardestTechnique(steps),
    };
  }
  return {
    status: 'solved',
    board: solvedBoard,
    steps,
    hardestTechnique: hardestTechnique(steps),
  };
}

export function solveWithHumanTechniques(board: Board): HumanSolveResult | null {
  const analysis = analyzeWithHumanTechniques(board);
  return analysis.status === 'solved'
    ? {
        board: analysis.board,
        steps: analysis.steps,
        hardestTechnique: analysis.hardestTechnique,
      }
    : null;
}

export function rateDifficulty(board: Board): DifficultyRating | null {
  const result = solveWithHumanTechniques(board);
  if (result === null) {
    return null;
  }
  const rank = techniqueOrder.indexOf(result.hardestTechnique);
  const difficulty: Difficulty =
    rank <= 1 ? 'easy' : rank <= MEDIUM_MAX_TECHNIQUE_INDEX ? 'medium' : 'hard';
  return {
    difficulty,
    hardestTechnique: result.hardestTechnique,
    stepCount: result.steps.length,
  };
}
